import { Box, Typography } from '@mui/material';
import type { DynamicInvoicePageCustomer, DynamicInvoicePageInvoice } from '@weft/api-definition';
import type { InvoiceReviewStatus } from '@weft/types';
import { INTEGRATION_PROVIDERS, INVOICE_REVIEW_STATUSES, type Currency, type Period } from '@weft/types';
import { Price } from 'apps/app/src/components/design/Price';
import { useSyncNetSuiteInvoice } from 'apps/app/src/hooks/requests/use-net-suite';
import { useActivatedIntegration } from 'apps/app/src/hooks/use-activated-integration';
import { useToggle } from 'apps/app/src/hooks/use-toggle';
import { formatDateToDMY } from 'apps/app/src/utils/date';
import { useMemo, useCallback } from 'react';
import type { QueryKey } from 'react-query';
import { AddInvoiceNotesForm } from '../dynamic-invoice-header-forms/AddInvoiceNotesForm';
import { AddInvoicePurchaseOrderForm } from '../dynamic-invoice-header-forms/AddInvoicePurchaseOrderForm';
import { DynamicInvoiceHeaderDotMenu } from './DynamicInvoiceHeaderDotMenu';

type InvoiceHeaderInfoProps = {
  customer: DynamicInvoicePageCustomer;
  invoice: DynamicInvoicePageInvoice;
  currency: Currency;
  queryKeyToInvalidate?: QueryKey;
};

export const InvoiceHeaderInfo: React.FC<InvoiceHeaderInfoProps> = ({ customer, invoice, currency, queryKeyToInvalidate }) => {
  const [isPurchaseOrderModalOpen, setPurchaseOrderModalOpen] = useToggle(false);
  const [isNotesModalOpen, setNotesModalOpen] = useToggle(false);
  const syncNetSuiteInvoiceMutation = useSyncNetSuiteInvoice();
  const isSlackActivated = useActivatedIntegration(INTEGRATION_PROVIDERS.slack, { suspense: true });
  const { id: invoiceId, reviewStatus } = invoice;

  const isSlackShareEnabled = useMemo(() => {
    const allowedStatusesForSlackShare: InvoiceReviewStatus[] = [
      INVOICE_REVIEW_STATUSES.InReview,
      INVOICE_REVIEW_STATUSES.Draft,
    ];
  
    return isSlackActivated && allowedStatusesForSlackShare.includes(reviewStatus);
  }, [reviewStatus, isSlackActivated]);

  const handleSyncNetsuiteToInvoice = useCallback( 
    () => syncNetSuiteInvoiceMutation.mutate({ invoicesIds: [invoiceId] }),
    [invoiceId, syncNetSuiteInvoiceMutation],
  );

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box>
        <Typography
          variant="h5"
          fontSize="24px"
          color="textPrimary"
          fontWeight={300}
        >
          {`${customer.name} — ${invoice.name}`}
        </Typography>
        <Typography
          variant="subtitle1"
          fontSize="16px"
          color="textSecondary"
          fontWeight={400}
        >
          {formatBillingCycle(invoice.billingCycle)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'right' }}>
          <Price
            sx={{ marginLeft: 'auto' }}
            variant="h6"
            currency={currency}
          >
            {invoice.revenueBreakdown.total}
          </Price>
          <Typography
            variant="subtitle1"
            fontSize="16px"
            color="textSecondary"
            fontWeight={400}
          >
            Total Amount
          </Typography>
        </Box>

        <DynamicInvoiceHeaderDotMenu
          onPurchaseOrderModalOpen={() => setPurchaseOrderModalOpen()}
          onNotesModalOpen={() => setNotesModalOpen()}
          onSyncInvoiceToNetSuite={handleSyncNetsuiteToInvoice}
          invoiceId={invoice.id}
          isSlackShareEnabled={isSlackShareEnabled}
        />

        <AddInvoicePurchaseOrderForm
          invoiceId={invoiceId}
          open={isPurchaseOrderModalOpen}
          onClose={() => setPurchaseOrderModalOpen()}
          existingPurchaseOrder={invoice.purchaseOrder}
          queryKeyToInvalidate={queryKeyToInvalidate}
        />

        <AddInvoiceNotesForm
          invoiceId={invoiceId}
          open={isNotesModalOpen}
          onClose={() => setNotesModalOpen()}
          existingNotes={invoice.notes}
          queryKeyToInvalidate={queryKeyToInvalidate}
        />

      </Box>
    </Box>
  );
};

function formatBillingCycle(period: Period) {
  const start = formatDateToDMY(period.startTime);
  const end = formatDateToDMY(period.endTime);
    
  return `${start} - ${end}`;
}